import { useEffect, useState } from "react";
import type { ProductPageKey } from "../../shared/contracts/product-pages.js";
import { rivalryCalendarSearch } from "../pages/rivalry/use-rivalry-calendar.js";
import { preserveAuthenticationLink } from "../lib/invite-link.js";

/** Pages the client can show: every server page plus the client-only More tab. */
export type ClientPageKey = ProductPageKey | "more";

const paths: Record<ClientPageKey, string> = {
  clubhouse: "/",
  "tee-off": "/tee-off",
  scorecard: "/scorecard",
  rounds: "/rounds",
  rivals: "/rivals",
  rivalry: "/rivalry",
  feed: "/feed",
  courses: "/courses",
  stats: "/stats",
  insights: "/rivalry",
  trophies: "/trophies",
  reviews: "/reviews",
  profile: "/profile",
  settings: "/settings",
  admin: "/admin",
  fgl: "/fgl",
  more: "/more",
};

const pagesByPath = new Map(
  Object.entries(paths).map(([page, path]) => [path, page as ClientPageKey]),
);
pagesByPath.set("/rivalry", "rivalry");

export interface ProductRoute {
  page: ClientPageKey;
  pathFor(page: ClientPageKey): string;
  navigate(page: ClientPageKey): void;
}

export function useProductRoute(): ProductRoute {
  const [page, setPage] = useState(() => pageFromLocation());
  useEffect(() => {
    const update = () => setPage(pageFromLocation());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return {
    page,
    pathFor: (target) => paths[target],
    navigate: (target) => {
      if (target === "insights") target = "rivalry";
      const path = paths[target];
      if (window.location.pathname !== path)
        window.history.pushState(null, "", calendarPath(target));
      setPage(target);
      window.scrollTo({ top: 0, behavior: "instant" });
    },
  };
}

function calendarPath(target: ClientPageKey): string {
  const path =
    target === "clubhouse" || target === "rivalry" || target === "insights"
      ? `${paths[target]}?${rivalryCalendarSearch()}`
      : paths[target];
  return preserveAuthenticationLink(path, window.location.search);
}

function pageFromLocation(): ClientPageKey {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/insights") {
    window.history.replaceState(
      window.history.state,
      "",
      `/rivalry${window.location.search}${window.location.hash}`,
    );
    return "rivalry";
  }
  return pagesByPath.get(path) ?? "clubhouse";
}
